// src/modules/app-views/_apps/appsindico/units/pages/Init/modals/UnitsParametersModal/tabs/BlocksTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Flex,
  Space,
  Popconfirm,
  Modal,
  Form,
  Input,
  InputNumber,
  ColorPicker,
  Row,
  Col,
  Divider,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BlocksTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  const [blocks, setBlocks] = useState(() => {
    const moduleId = storedModule?.id || 'units';
    const raw = appService.getModulePreferences({ moduleId, category: 'blocks' });
    return Array.isArray(raw) ? raw : [];
  });

  const visibleBlocks = useMemo(() => blocks.filter(b => !b.removed), [blocks]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (showModal) {
      if (editingRecord) {
        form.setFieldsValue({
          name: editingRecord.name || '',
          abbreviation: editingRecord.abbreviation || '',
          number: editingRecord.number ?? null,
          foreColor: editingRecord.foreColor || '#FFFFFF',
          backColor: editingRecord.backColor || '#1890ff',
          totalFloors: editingRecord.totalFloors ?? null,
          floorRanges: editingRecord.floorRanges?.length
            ? editingRecord.floorRanges
            : [{ from: 1, to: 1, unitsPerFloor: 1, label: '' }],
        });
      } else {
        form.setFieldsValue({
          foreColor: '#FFFFFF',
          backColor: '#1890ff',
          floorRanges: [{ from: 1, to: 1, unitsPerFloor: 1, label: '' }],
        });
      }
    }
  }, [showModal, editingRecord, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
    form.resetFields();
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const moduleId = storedModule?.id || 'units';

      const nameValue = (values.name || '').trim();
      const abbreviationValue = (values.abbreviation || '').trim().toUpperCase();
      const foreColorValue = resolveColor(values.foreColor, '#FFFFFF');
      const backColorValue = resolveColor(values.backColor, '#1890ff');
      const floorRanges = (values.floorRanges || []).map(r => ({
        from: r.from ?? 1,
        to: r.to ?? 1,
        unitsPerFloor: r.unitsPerFloor ?? 1,
        label: r.label || '',
      }));

      // Check for duplicate name
      const isDuplicate = blocks.some(
        b =>
          b.uid !== editingRecord?.uid &&
          !b.removed &&
          (b.name || '').toLowerCase() === nameValue.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);

      let updated;
      if (editingRecord) {
        updated = blocks.map(b =>
          b.uid === editingRecord.uid
            ? {
                ...b,
                name: nameValue,
                abbreviation: abbreviationValue,
                number: values.number ?? b.number,
                foreColor: foreColorValue,
                backColor: backColorValue,
                totalFloors: values.totalFloors ?? b.totalFloors,
                floorRanges,
              }
            : b
        );
      } else {
        updated = [
          ...blocks,
          {
            uid: generateUidV4(),
            name: nameValue,
            abbreviation: abbreviationValue,
            number: values.number ?? visibleBlocks.length + 1,
            foreColor: foreColorValue,
            backColor: backColorValue,
            totalFloors: values.totalFloors ?? 1,
            floorRanges,
          },
        ];
      }

      await appService.updateModulePreferences({ moduleId, category: 'blocks', data: updated });

      setBlocks(updated);
      appService.message({ flag: 's', text: 'saved_successfully' });
      setShowModal(false);
      setEditingRecord(null);
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // validation error
      console.error('[BlocksTab] Save error:', error);
      appService.message({ flag: 'e', text: 'error_saving' });
    } finally {
      setLoading(false);
    }
  }, [form, blocks, editingRecord, visibleBlocks.length, setLoading, storedModule?.id]);

  const handleDelete = useCallback(
    async record => {
      const moduleId = storedModule?.id || 'units';
      setLoading(true);
      try {
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'blocks',
          uid: record.uid,
        });

        let updated;
        if (isInUse) {
          updated = blocks.map(b =>
            b.uid === record.uid ? { ...b, removed: true } : b
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          updated = blocks.filter(b => b.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        await appService.updateModulePreferences({ moduleId, category: 'blocks', data: updated });
        setBlocks(updated);
      } catch (error) {
        console.error('[BlocksTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [blocks, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('name'),
      dataIndex: ['name'],
      render: (text, record) => (
        <Flex align="center" gap={8}>
          {record.backColor && (
            <span
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: record.backColor,
                border: '1px solid rgba(0,0,0,0.15)',
                flexShrink: 0,
              }}
            />
          )}
          {text}
        </Flex>
      ),
    },
    {
      title: translateX('abbreviation'),
      dataIndex: ['abbreviation'],
      width: 100,
      align: 'center',
    },
    {
      title: translateX('floors'),
      dataIndex: ['totalFloors'],
      width: 80,
      align: 'center',
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleBlocks}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_block')}
        </Button>
      </Flex>

      <Modal
        title={editingRecord ? translateX('edit') : translateX('add_block')}
        open={showModal}
        onCancel={handleCancel}
        destroyOnHidden
        centered
        mousePosition={mousePosition}
        getContainer={document.body}
        width={560}
        footer={
          <Flex justify="end" style={{ paddingTop: 8 }}>
            <Space>
              <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
                {translateX('cancel')}
              </Button>
              <Button type="primary" loading={loading} icon={<CheckOutlined />} onClick={handleSave}>
                {translateX('save')}
              </Button>
            </Space>
          </Flex>
        }
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          {/* Row 1: name, abbreviation, number */}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={translateX('name')}
                rules={[{ required: true, message: () => translateX('required_field') }]}
              >
                <Input maxLength={80} disabled={loading} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="abbreviation"
                label={translateX('abbreviation')}
                rules={[{ required: true, message: () => translateX('required_field') }]}
              >
                <Input maxLength={3} disabled={loading} style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="number" label={translateX('number')}>
                <InputNumber min={1} style={{ width: '100%' }} disabled={loading} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: foreColor, backColor */}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="foreColor" label={translateX('fore_color')}>
                <ColorPicker format="hex" disabled={loading} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="backColor" label={translateX('back_color')}>
                <ColorPicker format="hex" disabled={loading} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3: totalFloors */}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="totalFloors"
                label={translateX('total_floors')}
                rules={[{ required: true, message: () => translateX('required_field') }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} disabled={loading} />
              </Form.Item>
            </Col>
          </Row>

          {/* Floor ranges */}
          <Divider style={{ marginTop: 4, marginBottom: 12 }} />
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            {translateX('floor_ranges')}
          </Typography.Text>

          <Form.List name="floorRanges">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={8} key={key} align="middle">
                    <Col span={5}>
                      <Form.Item
                        {...restField}
                        name={[name, 'from']}
                        label={name === 0 ? translateX('from') : undefined}
                      >
                        <InputNumber min={1} style={{ width: '100%' }} disabled={loading} />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        {...restField}
                        name={[name, 'to']}
                        label={name === 0 ? translateX('to') : undefined}
                      >
                        <InputNumber min={1} style={{ width: '100%' }} disabled={loading} />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        {...restField}
                        name={[name, 'unitsPerFloor']}
                        label={name === 0 ? translateX('units_per_floor') : undefined}
                      >
                        <InputNumber min={1} style={{ width: '100%' }} disabled={loading} />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        {...restField}
                        name={[name, 'label']}
                        label={name === 0 ? translateX('label') : undefined}
                      >
                        <Input maxLength={30} disabled={loading} />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <MinusCircleOutlined
                        onClick={() => !loading && remove(name)}
                        style={{
                          marginTop: name === 0 ? 30 : 0,
                          color: loading ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.45)',
                          cursor: loading ? 'not-allowed' : 'pointer',
                        }}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ from: 1, to: 1, unitsPerFloor: 1, label: '' })}
                  block
                  icon={<PlusOutlined />}
                  disabled={loading}
                  style={{ marginTop: 4 }}
                >
                  {translateX('add_range')}
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BlocksTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Resolves ColorPicker value to hex string.
 * Handles both string values and ColorPicker object returns.
 */
function resolveColor(value, fallback) {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value?.toHexString?.() || value?.hex || fallback;
}
